// AGC: Title field renderer — text input with voice-to-text option
import React from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import { FieldMicButton } from './FieldMicButton'
import type { VoiceFieldProps } from './types'

export const TitleField: React.FC<VoiceFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  lastResponse,
  wizardMode,
  onPermissionBlocked,
}) => {
  const { t } = useTranslation()

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', input: 'px-4 py-3 text-lg', pill: 'text-sm px-3 py-2', icon: 'w-4 h-4', mb: 'mb-3', gap: 'gap-3' }
    : { card: 'p-2', label: 'text-xs', input: 'px-2 py-1.5 text-sm', pill: 'text-xs px-2 py-1', icon: 'w-3 h-3', mb: 'mb-1.5', gap: 'gap-2' }

  return (
    <div className={`${sz.card} bg-orange-500/10 border border-orange-500/30 rounded-xl`}>
      <div className={sz.mb}>
        <span className={`text-orange-300 ${sz.label} font-medium flex items-center gap-2`}>
          ✏️ {info.fieldLabel}
        </span>
        {info.reason && (
          <p className="text-white/40 text-sm mt-1">{info.reason}</p>
        )}
      </div>
      <div className={`flex items-center ${sz.gap}`}>
        <input
          type="text"
          placeholder={t('voice.taskTitlePlaceholder')}
          value={inputValues.title || ''}
          onChange={(e) => setInputValues(prev => ({ ...prev, title: e.target.value }))}
          className={`flex-1 ${sz.input} bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-orange-500`}
        />
        {info.allowVoiceInput && (
          <FieldMicButton
            fieldName="title"
            variant="orange"
            onResult={(transcript) => setInputValues(prev => ({ ...prev, title: transcript }))}
            onPermissionBlocked={onPermissionBlocked}
          />
        )}
      </div>
      {inputValues.title && (
        <div className={`mt-2 flex items-center gap-2 ${sz.pill} text-orange-300 bg-orange-500/10 rounded-lg`}>
          <Check className={sz.icon} />
          {lastResponse?.entryType === 'task'
            ? t('voice.taskTitle')
            : lastResponse?.entryType === 'revenue'
              ? t('voice.revenueTitle')
              : t('voice.expenseTitle')
          }: <strong>{inputValues.title}</strong>
        </div>
      )}
    </div>
  )
}
